// Halaman "File Karyawan" — dokumen per karyawan (KTP, Ijazah, Kontrak, dst).
//
// Sebelumnya halaman ini rusak total: memanggil endpoint Procurement yang
// tak berhubungan (lihat audit modul HR, Finding #1). Ditulis ulang sebagai
// fitur baru yang genuinely berfungsi — backend/tabel dokumen karyawan
// sebelumnya memang belum pernah ada.
import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import Modal from "@material-tailwind/react/Modal";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import Button from "@material-tailwind/react/Button";
import Card from "@material-tailwind/react/Card";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

import { Colors } from "../../../assets/Colors";
import { isRequired, useForm } from "../../../components/isRequired";
import { KaryawanSelectorBuilder } from "./KaryawanSelector";
import Skeltons from "../../../components/loading/Skeltons";
import {
  fetchPost,
  fetchPostSilance,
  fetchPostMultipartSeaweed,
} from "../../../fetchers";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Jenis dokumen — daftar tetap (disepakati: dropdown, bukan teks bebas).
const JENIS_DOKUMEN_OPTIONS = [
  "KTP",
  "KK",
  "Ijazah",
  "Sertifikat",
  "KontrakKerja",
  "NPWP",
  "BPJS",
  "SIM",
  "Lainnya",
];
const jenisDokumenLabel = (v) => (v === "KontrakKerja" ? "Kontrak Kerja" : v);

const toDateInputStr = (d) =>
  d instanceof Date && !isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    : "";

// Unduh file lampiran — pola sama persis dengan HrdReportModal.js (fetch blob
// dari file_upload service, bukan arsitektur penyimpanan/unduh baru).
const downloadFile = (namaFile) => {
  if (!namaFile) return;
  const fileUrl =
    (process.env.REACT_APP_API_UPLOAD_URL || "/") +
    "file/" +
    encodeURIComponent(namaFile);
  fetch(fileUrl, { method: "GET", headers: { Accept: "application/octet-stream" } })
    .then((r) => {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = namaFile.replace(/^[0-9a-fA-F-]{36}-/, ""); // buang prefix UUID saat disimpan
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => toast.error("Gagal mengunduh file", { position: "bottom-right" }));
};

const KaryawanFile = () => {
  const [nik, setNik] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentData, setCurrentData] = useState({});
  const [tanggalTerbit, setTanggalTerbit] = useState("");
  const [tanggalBerlaku, setTanggalBerlaku] = useState("");

  const initialState = { JenisDokumen: "", NomorDokumen: "", Catatan: "", NamaFile: "" };
  const validations = [
    ({ JenisDokumen }) =>
      isRequired(JenisDokumen) || { JenisDokumen: "Jenis dokumen wajib dipilih" },
  ];
  const { values, errors, touched, changeHandler, customHandler, clearHandler } =
    useForm(initialState, validations);

  const listMutation = useMutation((n) => fetchPost("getKaryawanFile", { Nik: n }));
  const insertMutation = useMutation((fd) => fetchPostSilance("insertKaryawanFile", fd));
  const updateMutation = useMutation((fd) => fetchPostSilance("updateKaryawanFile", fd));
  const deleteMutation = useMutation((fd) => fetchPostSilance("deleteKaryawanFile", fd));
  const fileMutation = useMutation((fd) => fetchPostMultipartSeaweed(fd));

  const refetch = (n) => {
    let fd = new FormData();
    fd.append("Nik", n);
    listMutation.mutate(fd);
  };

  const onPickKaryawan = (value, field) => {
    if (field === "Nik") {
      setNik(value);
      refetch(value);
    }
  };

  const openTambah = () => {
    clearHandler();
    setTanggalTerbit("");
    setTanggalBerlaku("");
    setIsEdit(false);
    setCurrentData({});
    setShowModal(true);
  };
  const openEdit = (row) => {
    clearHandler();
    customHandler(row.JenisDokumen, "JenisDokumen");
    customHandler(row.NomorDokumen || "", "NomorDokumen");
    customHandler(row.Catatan || "", "Catatan");
    setTanggalTerbit(row.TanggalTerbit ? new Date(row.TanggalTerbit) : "");
    setTanggalBerlaku(row.TanggalBerlaku ? new Date(row.TanggalBerlaku) : "");
    setIsEdit(true);
    setCurrentData(row);
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    clearHandler();
  };

  const doDelete = (row) => {
    if (!window.confirm(`Hapus dokumen "${jenisDokumenLabel(row.JenisDokumen)}" ini?`)) return;
    let fd = new FormData();
    fd.append("KaryawanFileID", row.KaryawanFileID);
    deleteMutation.mutate(fd, {
      onSuccess: () => {
        toast.success("Dokumen dihapus", { position: "bottom-right", autoClose: 3000 });
        refetch(nik);
      },
      onError: (e) =>
        toast.error(e?.message || "Gagal menghapus dokumen", { position: "bottom-right" }),
    });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!values.JenisDokumen) return;

    const finishInsert = (namaFile) => {
      let fd = new FormData();
      fd.append("Nik", nik);
      fd.append("JenisDokumen", values.JenisDokumen);
      fd.append("NomorDokumen", values.NomorDokumen || "");
      fd.append("TanggalTerbit", toDateInputStr(tanggalTerbit));
      fd.append("TanggalBerlaku", toDateInputStr(tanggalBerlaku));
      fd.append("Catatan", values.Catatan || "");
      fd.append("NamaFile", namaFile);
      insertMutation.mutate(fd, {
        onSuccess: () => {
          toast.success("Dokumen ditambahkan", { position: "bottom-right", autoClose: 3000 });
          setShowModal(false);
          clearHandler();
          refetch(nik);
        },
        onError: (err) =>
          toast.error(err?.message || "Gagal menambahkan dokumen", { position: "bottom-right" }),
      });
    };

    if (isEdit) {
      let fd = new FormData();
      fd.append("KaryawanFileID", currentData.KaryawanFileID);
      fd.append("JenisDokumen", values.JenisDokumen);
      fd.append("NomorDokumen", values.NomorDokumen || "");
      fd.append("TanggalTerbit", toDateInputStr(tanggalTerbit));
      fd.append("TanggalBerlaku", toDateInputStr(tanggalBerlaku));
      fd.append("Catatan", values.Catatan || "");
      updateMutation.mutate(fd, {
        onSuccess: () => {
          toast.success("Dokumen diperbarui", { position: "bottom-right", autoClose: 3000 });
          setShowModal(false);
          clearHandler();
          refetch(nik);
        },
        onError: (err) =>
          toast.error(err?.message || "Gagal memperbarui dokumen", { position: "bottom-right" }),
      });
      return;
    }

    // Tambah baru: file wajib. Upload dulu (pola UUID sama persis dgn LemburPage.js),
    // baru simpan record dengan nama file yang dihasilkan.
    if (!values.NamaFile || !values.NamaFile.name) {
      toast.error("File dokumen wajib dipilih", { position: "bottom-right" });
      return;
    }
    try {
      const { v4: uuidv4 } = require("uuid");
      const uuid = uuidv4();
      const nama = uuid + "-" + values.NamaFile.name.replaceAll(" ", "_");
      let uploadFd = new FormData();
      uploadFd.append("file", values.NamaFile, nama);
      fileMutation.mutate(uploadFd, {
        onSuccess: () => finishInsert(nama),
        onError: () => toast.error("Gagal mengunggah file", { position: "bottom-right" }),
      });
    } catch (err) {
      toast.error("Gagal memproses file", { position: "bottom-right" });
    }
  };

  const rows = Array.isArray(listMutation.data?.data) ? listMutation.data.data : [];

  // ── Export (dokumen karyawan yang sedang dipilih) ─────────────
  const exportHeader = ["Jenis Dokumen", "No. Dokumen", "Tanggal Terbit", "Tanggal Berlaku", "Catatan"];
  const exportBody = () =>
    rows.map((r) => [
      jenisDokumenLabel(r.JenisDokumen) || "",
      r.NomorDokumen || "",
      r.TanggalTerbit || "",
      r.TanggalBerlaku || "",
      r.Catatan || "",
    ]);
  const handleDownloadExcel = () => {
    downloadExcel({
      fileName: "File_Karyawan_" + nik,
      sheet: "data",
      tablePayload: { header: exportHeader, body: exportBody() },
    });
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(13);
    doc.text("File Karyawan — " + nik, 14, 14);
    autoTable(doc, {
      head: [exportHeader],
      body: exportBody(),
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 58, 95] },
    });
    doc.save("File_Karyawan_" + nik + ".pdf");
  };

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
        </div>
      </div>
      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="px-4 pt-4">
                <KaryawanSelectorBuilder value={nik} onChange={onPickKaryawan} />
              </div>

              {!nik ? (
                <div className="text-center py-8 text-gray-400">
                  Pilih karyawan dulu untuk melihat dokumennya.
                </div>
              ) : listMutation.isLoading ? (
                <div className="w-full pt-0">
                  <Skeltons />
                </div>
              ) : (
                <>
                  <div className="flex justify-between px-4 pb-2 flex-wrap gap-2">
                    <button
                      onClick={openTambah}
                      className="flex items-center justify-center bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow"
                    >
                      Tambah Dokumen +
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={handleDownloadExcel}
                        className="flex items-center justify-center bg-white text-gray-800 font-semibold py-2 px-4 border border-green-600 hover:bg-green-600 hover:text-white rounded shadow"
                      >
                        Download Excel
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="flex items-center justify-center bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow"
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto px-4 pb-4">
                    <table className="table-auto w-full text-sm text-left text-gray-500">
                      <thead className="text-xs text-orange-600 uppercase bg-gray-50">
                        <tr>
                          <th className="py-3 px-6">Jenis Dokumen</th>
                          <th className="py-3 px-6">No. Dokumen</th>
                          <th className="py-3 px-6">Tanggal Terbit</th>
                          <th className="py-3 px-6">Tanggal Berlaku</th>
                          <th className="py-3 px-6">Catatan</th>
                          <th className="py-3 px-6">File</th>
                          <th className="py-3 px-6">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-8 text-gray-400">
                              Belum ada dokumen untuk karyawan ini
                            </td>
                          </tr>
                        ) : (
                          rows.map((r, i) => (
                            <tr key={r.KaryawanFileID} className={i % 2 === 0 ? "bg-gray-50 border-b" : "bg-white border-b"}>
                              <td className="py-4 px-6">{jenisDokumenLabel(r.JenisDokumen)}</td>
                              <td className="py-4 px-6">{r.NomorDokumen || "-"}</td>
                              <td className="py-4 px-6 whitespace-nowrap">{r.TanggalTerbit || "-"}</td>
                              <td className="py-4 px-6 whitespace-nowrap">{r.TanggalBerlaku || "-"}</td>
                              <td className="py-4 px-6">{r.Catatan || "-"}</td>
                              <td className="py-4 px-6">
                                <button
                                  onClick={() => downloadFile(r.NamaFile)}
                                  className="text-blue-600 hover:underline"
                                >
                                  Unduh
                                </button>
                              </td>
                              <td className="py-4 px-6 whitespace-nowrap">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => openEdit(r)}
                                    className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-1 px-3 border border-gray-400 rounded shadow text-sm"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => doDelete(r)}
                                    className="bg-white hover:bg-red-50 text-red-600 font-semibold py-1 px-3 border border-red-300 rounded shadow text-sm"
                                  >
                                    Hapus
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Modal size="regular" active={showModal} toggler={() => setShowModal(true)}>
        <ModalHeader className="mt-20" toggler={closeModal}>
          {isEdit ? "Edit Dokumen" : "Tambah Dokumen"}
        </ModalHeader>
        <ModalBody>
          <form className="w-full max-w-lg" onSubmit={submit}>
            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Jenis Dokumen *
                </label>
                <select
                  value={values.JenisDokumen}
                  onChange={(e) => customHandler(e.target.value, "JenisDokumen")}
                  className={
                    touched.JenisDokumen && errors.JenisDokumen
                      ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight outline-none bg-white"
                      : "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  }
                >
                  <option value="">-- Pilih --</option>
                  {JENIS_DOKUMEN_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {jenisDokumenLabel(o)}
                    </option>
                  ))}
                </select>
                {touched.JenisDokumen && errors.JenisDokumen && (
                  <p className="text-red-500 text-xs italic">{errors.JenisDokumen}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nomor Dokumen
                </label>
                <input
                  value={values.NomorDokumen}
                  onChange={changeHandler}
                  name="NomorDokumen"
                  type="text"
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                />
              </div>
            </div>

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tanggal Terbit
                </label>
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={tanggalTerbit || null}
                  onChange={(d) => setTanggalTerbit(d)}
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  isClearable
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tanggal Berlaku
                </label>
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={tanggalBerlaku || null}
                  onChange={(d) => setTanggalBerlaku(d)}
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  isClearable
                />
              </div>
            </div>

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Catatan
                </label>
                <input
                  value={values.Catatan}
                  onChange={changeHandler}
                  name="Catatan"
                  type="text"
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                />
              </div>
            </div>

            {!isEdit && (
              <div className="flex flex-wrap -mx-3">
                <div className="w-full px-3 mb-6">
                  <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                    File Dokumen *
                  </label>
                  <label
                    htmlFor="NamaFileKaryawan"
                    className="block w-full border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500 cursor-pointer"
                  >
                    {values.NamaFile && values.NamaFile.name ? (
                      <span>File dipilih — {values.NamaFile.name}</span>
                    ) : (
                      <span>Pilih file...</span>
                    )}
                  </label>
                  <input
                    type="file"
                    id="NamaFileKaryawan"
                    style={{ visibility: "hidden", position: "absolute" }}
                    onChange={(e) => customHandler(e.target.files[0], "NamaFile")}
                  />
                </div>
              </div>
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="red" buttonType="link" onClick={closeModal} ripple="dark">
            Batal
          </Button>
          <Button
            color={values.JenisDokumen ? "green" : "blueGray"}
            onClick={submit}
            ripple={values.JenisDokumen ? "light" : ""}
          >
            SIMPAN
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KaryawanFile;
